import {
  createContext,
  useEffect,
  useReducer,
  useRef,
  type Dispatch
} from "react";

import { useSendWebEvent } from "~/hooks";
import type { NativeEvent } from "~/types/events";
import { useCreateCashDrawerOpenPosLog } from "~/api/logs/hooks/use-create-cash-drawer-open-pos-log";
import { getWebDeviceId, getWebTillId, isWebBrowser } from "~/utils/web";
import { type Printer } from "~/types/hardware";
import { logger } from "~/services/logger";
import { toast } from "~/components/hooks/use-toast";
import { useT } from "@repo/transifex";
import type { CashDrawerTrigger } from "~/api/logs/requests";

import { authActions, getAuth } from "../store/auth";

import { initialState, reducer } from "./reducer";
import type { AppAction, AppState, FreedompayConnectionStatus } from "./types";

const DEVICE_DATA_RETRY_MS = 3000;

const FAKE_PRINTER: Printer = {
  model: "Virtual Cash Drawer (FAKE)",
  emulation: "virtual",
  identifier: "fake-cash-drawer",
  interfaceType: "emulator",
  hasCashDrawer: true
};

export const AppContext = createContext<AppState>(initialState);
export const AppDispatchContext = createContext<Dispatch<AppAction>>(
  () => null
);

type AppProviderProps = {
  children: React.ReactNode;
};
export const AppProvider = ({ children }: AppProviderProps) => {
  const { mutate: createPosLog } = useCreateCashDrawerOpenPosLog();
  const t = useT();

  const cashDrawerTriggerRef = useRef<CashDrawerTrigger | null>(null);
  const lastToastedFccStatus = useRef<FreedompayConnectionStatus | null>(null);

  const middleware = (action: AppAction) => {
    switch (action.type) {
      case "CASH_DRAWER_OPEN_TRIGGER": {
        cashDrawerTriggerRef.current = action.payload.trigger;
        break;
      }

      case "CASH_DRAWER_OPEN_SUCCESS": {
        createPosLog(cashDrawerTriggerRef.current ?? "automatic");
        cashDrawerTriggerRef.current = null;
        break;
      }

      case "ONLINE_STATUS_SUCCESS": {
        logger.trace(
          `[Network] Till is ${action.payload ? "online" : "offline"}`,
          {
            is_online: action.payload
          }
        );
        authActions.updateOnlineStatus(action.payload);
        break;
      }

      // CASH_DRAWER_STATUS_ERROR is left out on purpose: the wrapper polls the
      // drawer every second, so a dead printer would flood Sentry.
      case "PRINTER_ERROR":
      case "PRINTER_COMMUNICATION_ERROR":
      case "PRINTER_PRINT_RECEIPT_ERROR":
      case "CASH_DRAWER_OPEN_ERROR": {
        logger.warn("[Printer] Error", { printer_error: action.payload });
        break;
      }

      case "FREEDOMPAY_OPEN_SETTINGS_ERROR": {
        toast({
          title: t("Could not open FreedomPay settings"),
          description: action.payload.error,
          variant: "destructive"
        });
        break;
      }

      case "FREEDOMPAY_FCC_STATUS_UPDATE": {
        if (lastToastedFccStatus.current === action.payload.status) break;
        lastToastedFccStatus.current = action.payload.status;

        const statusConfig = {
          connected: {
            title: t("FreedomPay FCC connected"),
            variant: "success" as const
          },
          disconnected: {
            title: t("FreedomPay FCC disconnected"),
            variant: "destructive" as const
          },
          reconnecting: {
            title: t("Reconnecting terminal..."),
            variant: "warning" as const
          }
        };
        const config = statusConfig[action.payload.status];
        toast({ title: config.title, variant: config.variant });
        break;
      }

      case "FREEDOMPAY_POI_STATUS_UPDATE": {
        if (action.payload.status === "connected") {
          toast({
            title: t("FreedomPay POI connected"),
            description: action.payload.poi.name,
            variant: "success"
          });
        } else {
          toast({
            title: t("FreedomPay POI disconnected"),
            description: action.payload.poi.name,
            variant: "destructive"
          });
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  const [state, dispatch] = useReducerWithMiddleware(
    reducer,
    initialState,
    middleware
  );

  const sendWebEvent = useSendWebEvent();

  useNativeEvents(dispatch);

  useEffect(() => {
    sendWebEvent({ type: "DEVICE_DATA_GET_REQUEST" });

    // WEB - read local storage for till id and device id
    if (isWebBrowser()) {
      const deviceId = getWebDeviceId();
      const tillId = getWebTillId();

      const { isKanplaAdmin } = getAuth();

      dispatch({
        type: "DEVICE_DATA_GET_SUCCESS",
        payload: {
          deviceId,
          tillId,
          scale: null,
          os: "web",
          osVersion: window.navigator.userAgent,
          printer: isKanplaAdmin ? FAKE_PRINTER : null
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we olny need it to run once
  }, []);

  // The wrapper answers only while its listener is mounted, so a WebView
  // reload can drop the single request above. Keep asking until an id
  // arrives; since #198 the wrapper also pushes unprompted on load.
  // `os` is null until the first DEVICE_DATA_GET_SUCCESS on either path, so
  // it marks "a response arrived" - what gets lost is the handshake, not the
  // id, and a wrapper that answers with a null id must not be asked forever.
  const deviceDataReceived = state.device.os !== null;
  useEffect(() => {
    if (isWebBrowser() || deviceDataReceived) {
      return;
    }

    const interval = setInterval(() => {
      sendWebEvent({ type: "DEVICE_DATA_GET_REQUEST" });
    }, DEVICE_DATA_RETRY_MS);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendWebEvent is stable
  }, [deviceDataReceived]);

  return (
    <AppContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
};

/**
 * useReducer wrapper with middleware
 * @returns {[AppState, Dispatch<AppAction>]}
 */
const useReducerWithMiddleware = (
  reducer: (state: AppState, action: AppAction) => AppState,
  initialState: AppState,
  middlewareFn: (action: AppAction) => void
): [AppState, Dispatch<AppAction>] => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const dispatchWithMiddleware = (action: AppAction) => {
    middlewareFn(action);
    dispatch(action);
  };

  return [state, dispatchWithMiddleware];
};

/**
 * Checks if the event is native
 * @param event - intercepted post message
 * @returns true if the event is native
 */
const isNativeEvent = (event: unknown): event is NativeEvent => {
  return Boolean(event) && typeof (event as NativeEvent).type === "string";
};

type ParseNativeEventResult =
  | {
      event: NativeEvent;
      error: null;
    }
  | {
      event: null;
      error: string;
    };

/**
 * Parse the native event data
 * Window `message` events come from many sources (Firebase Auth's iframe
 * posts `!_{...}` framed payloads, browser extensions, devtools, etc.).
 * Cheap shape check + try/catch so we only parse plausible native events.
 * @param data - the data to parse
 * @returns the parsed event or an error
 */
function parseNativeEvent(data: string): ParseNativeEventResult {
  const first = data[0];
  if (first !== "{" && first !== "[") {
    return { event: null, error: "Invalid JSON data" };
  }

  try {
    const parsedData = JSON.parse(data) as NativeEvent;
    if (!isNativeEvent(parsedData)) {
      return { event: null, error: "Invalid NativeEvent data" };
    }

    return { event: parsedData, error: null };
  } catch (error) {
    return { event: null, error: String(error) };
  }
}

/**
 * Handle communication from native -> web and listen for the events coming from the native wrapper
 */
export const useNativeEvents = (eventHandler: (event: NativeEvent) => void) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    /**
     * Handle the post messages coming from the native app
     * @param postMessage - intercepted post message
     */
    const handleMessage = ({ data }: MessageEvent) => {
      if (typeof data !== "string") return;

      const { event, error } = parseNativeEvent(data);
      if (error !== null) return;

      eventHandler(event);
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage as EventListener);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we olny need it to run once
  }, []);
};
